/**
 * Offline sign-in: the grant a till keeps for each cashier (ARCHITECTURE.md §7.9).
 *
 * A grant is written when a cashier signs in **with signal** on this till, so
 * the only PINs a till will accept offline are ones the server has already
 * accepted here. It expires after `GRANT_DAYS`; after that the cashier needs
 * signal once to sign in again. It is dropped as soon as the server is seen to
 * reject a PIN the grant would still accept, which is how a changed PIN or a
 * deactivated cashier reaches a till.
 *
 * The lockout mirrors the server's ladder in `apps/accounts/services/pin.py`,
 * so guessing a PIN at an offline till is no easier than at an online one.
 */

import { db, getSyncMeta } from "@/db/database";
import type { OfflineGrant, StoredSession } from "@/db/schema";

/** §7.9's default window, until the till has heard the shop's own setting. */
export const GRANT_DAYS = 30;

/** The shop's `offline_grant_days`, as of the last catalogue pull. */
async function grantDays(): Promise<number> {
  try {
    return (await getSyncMeta()).tillSettings?.offlineGrantDays ?? GRANT_DAYS;
  } catch {
    return GRANT_DAYS;
  }
}

/**
 * PBKDF2-SHA256 rounds. A PIN has at most a million values, so no count makes a
 * copied grant safe; this one makes each guess cost a few hundred milliseconds
 * on the cheap tablets tills run on, while a cashier signing in only waits
 * about that long.
 */
const ITERATIONS = 300_000;

/** After this many wrong PINs the grant locks, for the matching step below. */
const LOCK_AFTER = 5;
const LOCK_STEPS_MS = [30_000, 2 * 60_000, 15 * 60_000, 60 * 60_000];

export type OfflineCheck =
  | { ok: true; session: StoredSession }
  | { ok: false; reason: "NO_GRANT" | "EXPIRED" | "WRONG_PIN" }
  | { ok: false; reason: "LOCKED"; until: string };

export const offlineGrantRepository = {
  /** Record that this cashier may sign in here offline. Called after an online PIN sign-in. */
  async remember(session: StoredSession, pin: string): Promise<void> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const now = new Date();
    const { key: _key, accessToken: _a, refreshToken: _r, offline: _o, ...rest } = session;

    const grant: OfflineGrant = {
      userId: session.user.id,
      staffCode: session.user.staffCode.toUpperCase(),
      session: rest,
      verifier: {
        salt: toBase64(salt),
        iterations: ITERATIONS,
        hash: toBase64(await derive(pin, salt, ITERATIONS)),
      },
      grantedAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + (await grantDays()) * 24 * 3600_000).toISOString(),
      failedAttempts: 0,
      lockedUntil: null,
    };
    await db.offlineGrants.put(grant);
  },

  /** Check a staff code and PIN against this till's grants, with no server. */
  async check(staffCode: string, pin: string): Promise<OfflineCheck> {
    const grant = await db.offlineGrants.where("staffCode").equals(staffCode.toUpperCase()).first();
    if (!grant) return { ok: false, reason: "NO_GRANT" };

    const now = Date.now();
    if (new Date(grant.expiresAt).getTime() <= now) {
      await db.offlineGrants.delete(grant.userId);
      return { ok: false, reason: "EXPIRED" };
    }
    if (grant.lockedUntil && new Date(grant.lockedUntil).getTime() > now) {
      return { ok: false, reason: "LOCKED", until: grant.lockedUntil };
    }

    if (!(await matches(grant, pin))) {
      const failedAttempts = grant.failedAttempts + 1;
      const step = Math.floor(failedAttempts / LOCK_AFTER) - 1;
      const lockedUntil =
        failedAttempts % LOCK_AFTER === 0
          ? new Date(now + LOCK_STEPS_MS[Math.min(step, LOCK_STEPS_MS.length - 1)]!).toISOString()
          : grant.lockedUntil;
      await db.offlineGrants.update(grant.userId, { failedAttempts, lockedUntil });
      return lockedUntil && new Date(lockedUntil).getTime() > now
        ? { ok: false, reason: "LOCKED", until: lockedUntil }
        : { ok: false, reason: "WRONG_PIN" };
    }

    await db.offlineGrants.update(grant.userId, { failedAttempts: 0, lockedUntil: null });
    return {
      ok: true,
      session: {
        ...grant.session,
        key: "current",
        accessToken: "",
        refreshToken: "",
        offline: true,
        signedInAt: new Date().toISOString(),
      },
    };
  },

  /**
   * The server just refused this staff code and PIN. If the grant would still
   * have accepted them, the PIN was changed or the cashier was stopped, and the
   * grant must not outlive that. A grant that would also have refused them is a
   * plain typo, and is kept.
   */
  async revokeIfStale(staffCode: string, pin: string): Promise<void> {
    const grant = await db.offlineGrants.where("staffCode").equals(staffCode.toUpperCase()).first();
    if (grant && (await matches(grant, pin))) await db.offlineGrants.delete(grant.userId);
  },

  /**
   * Who can sign in on this till right now with no signal: everybody holding an
   * unexpired grant here. For the sign-in screen's profile picker offline, so it
   * offers exactly the people it will let in.
   */
  async signInable(): Promise<{ staffCode: string; displayName: string }[]> {
    const now = Date.now();
    return (await db.offlineGrants.toArray())
      .filter((grant) => new Date(grant.expiresAt).getTime() > now)
      .map((grant) => ({ staffCode: grant.staffCode, displayName: grant.session.user.displayName }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  },

  /** Drop this cashier's grant, whatever the PIN. */
  async forget(userId: string): Promise<void> {
    await db.offlineGrants.delete(userId);
  },

  /** Every grant on this till. For when the till's pairing is lost. */
  async forgetAll(): Promise<void> {
    await db.offlineGrants.clear();
  },
};

async function matches(grant: OfflineGrant, pin: string): Promise<boolean> {
  const derived = await derive(pin, fromBase64(grant.verifier.salt), grant.verifier.iterations);
  return constantTimeEqual(derived, fromBase64(grant.verifier.hash));
}

async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(pin), "PBKDF2", false, [
    "deriveBits",
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations },
    key,
    256,
  );
  return new Uint8Array(bits);
}

function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let difference = 0;
  for (let i = 0; i < a.length; i++) difference |= a[i]! ^ b[i]!;
  return difference === 0;
}

function toBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function fromBase64(text: string): Uint8Array {
  return Uint8Array.from(atob(text), (char) => char.charCodeAt(0));
}
